"use client";

// Built on Base UI's unstyled Dialog — see full API at:
// https://base-ui.com/react/components/dialog

import React from "react";
import { X } from "lucide-react";

import { Dialog as BaseDialog } from "@base-ui/react/dialog";
import { ScrollArea } from "@base-ui/react/scroll-area";

import { cn } from "@marketing-sites/utils/cn";
import { getAnalyticsRegion } from "@marketing-sites/analytics/get-analytics-region";
import { triggerAnalyticsClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { TrackingType } from "@marketing-sites/analytics/types";

import { useAutofocusFirstField } from "../use-autofocus-first-field";

export interface RootProps extends BaseDialog.Root.Props {
  /** Overrides the isolation wrapper's block display — pass `inline` so a
   *  text-link trigger flows with the prose around it instead of breaking onto
   *  its own line. */
  wrapperClassName?: string;
  openMetadata: { analyticsId: string };
  closeMetadata: { analyticsId: string };
  /** Explicit analytics location for trigger-less modals. When omitted the
   *  location is resolved from the DOM (correct for modals with a Trigger). */
  analyticsLocation?: string;
}

export function Root({
  onOpenChange,
  wrapperClassName,
  openMetadata,
  closeMetadata,
  analyticsLocation,
  children,
  ...rest
}: RootProps) {
  const handleOpenChange: typeof onOpenChange = (open, event) => {
    const location =
      analyticsLocation ??
      getAnalyticsRegion(event.trigger ?? event.event?.target ?? null) ??
      "Unknown";
    const text = `Modal ${open ? "Opened" : "Closed"}`;

    triggerAnalyticsClick({
      text,
      location,
      type: TrackingType.Button,
      analyticsId: open ? openMetadata.analyticsId : closeMetadata.analyticsId,
    });

    onOpenChange?.(open, event);
  };

  return (
    <div className={cn("isolate", wrapperClassName)}>
      <BaseDialog.Root onOpenChange={handleOpenChange} {...rest}>
        {children}
      </BaseDialog.Root>
    </div>
  );
}

export const Trigger = BaseDialog.Trigger;
export const Title = BaseDialog.Title;
export const Description = BaseDialog.Description;
export const Close = BaseDialog.Close;

// Intercept the Portal and bind it to our nested Layouts rather than body tag
// This allows for fonts to correctly work
export function Portal({
  container,
  ...rest
}: React.ComponentProps<typeof BaseDialog.Portal>) {
  const rootRef = React.useRef<HTMLElement | null>(null);

  if (typeof document !== "undefined" && !rootRef.current && !container) {
    rootRef.current = document.querySelector(".root");
  }

  return <BaseDialog.Portal container={container ?? rootRef} {...rest} />;
}

export function Backdrop({
  className,
  ...rest
}: React.ComponentProps<typeof BaseDialog.Backdrop>) {
  return (
    <BaseDialog.Backdrop
      className={cn(
        "z-overlay fixed inset-0 isolate bg-black opacity-75",
        className,
      )}
      {...rest}
    />
  );
}

export function Viewport({
  className,
  ...rest
}: React.ComponentProps<typeof BaseDialog.Viewport>) {
  return (
    <BaseDialog.Viewport
      className={cn(
        "z-modal fixed inset-0 flex items-center justify-center overflow-hidden",
        className,
      )}
      {...rest}
    />
  );
}

const DefaultCloseButton = (
  <BaseDialog.Close
    aria-label="Close"
    className="z-modal-control absolute right-3 top-3 cursor-pointer"
  >
    <X className="size-5" />
  </BaseDialog.Close>
);

export function Popup({
  className,
  children,
  closeButton = DefaultCloseButton,
  initialFocus,
  scrollable = true,
  ...rest
}: React.ComponentProps<typeof BaseDialog.Popup> & {
  closeButton?: React.ReactNode;
  /** Wrap children in a padded, scrolling ScrollArea. Set `false` for
   *  full-bleed content that owns its own overflow (e.g. a video modal) —
   *  the padding goes with it, and overflow clips rather than scrolls. */
  scrollable?: boolean;
}) {
  const autofocus = useAutofocusFirstField();

  return (
    <BaseDialog.Popup
      className={cn(
        "relative flex max-h-[calc(100vh-2rem)] min-h-0 w-[min(30rem,calc(100vw-2rem))] max-w-full flex-col overflow-hidden bg-white",
        className,
      )}
      initialFocus={initialFocus ?? autofocus.initialFocus}
      {...rest}
    >
      {/* Custom: overridable close button default */}
      {closeButton}

      {/* Custom: inner scroll overflow, unless the caller opted out */}
      {!scrollable ? (
        <div ref={autofocus.contentRef}>{children}</div>
      ) : (
        <ScrollArea.Root className="relative flex min-h-0 flex-1 overflow-hidden p-4 pt-10 md:pt-4">
          <ScrollArea.Viewport className="min-h-0 flex-1 overflow-y-auto overscroll-contain focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-blue-800">
            <ScrollArea.Content ref={autofocus.contentRef}>
              {children}
            </ScrollArea.Content>
          </ScrollArea.Viewport>

          <ScrollArea.Scrollbar className="data-hovering:pointer-events-auto data-scrolling:pointer-events-auto data-hovering:opacity-100 data-scrolling:opacity-100 data-hovering:duration-75 data-scrolling:duration-75 pointer-events-none absolute mx-1 my-2 flex w-1 justify-center rounded-full opacity-0 transition-opacity duration-300">
            <ScrollArea.Thumb className="w-full rounded-[inherit] bg-black/20 before:absolute before:left-1/2 before:top-1/2 before:h-[calc(100%+1rem)] before:w-[calc(100%+1rem)] before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']" />
          </ScrollArea.Scrollbar>
        </ScrollArea.Root>
      )}
    </BaseDialog.Popup>
  );
}
